<template>
  <div
    :class="{
      'menu-body': true,
      'menu-body--mobile': isMobile,
    }"
  >
    <div class="top justify-space-between align-center">
      <ui-checkbox
        :color="$colors.gray40"
        v-model="isSelectedAll"
        class="pb-0 align-center"
      >
        <template #label>
          <div class="list-item-text" :style="{ color: textColor }">
            {{ t('All workspaces') }}
          </div>
        </template>
      </ui-checkbox>
      <div>
        <ui-tooltip-icon
          v-if="!isMobile"
          preset="help"
          :color="textColor"
          class="pr-0"
          :size="14"
        >
          <span
            v-html="
              t(
                'All the elements belong to Workspaces.<br> You can set up your own Workspaces. Public and Master Workspaces are available by default',
              )
            "
          ></span>
        </ui-tooltip-icon>
        <router-link
          v-if="!store.user.isMember"
          @click.prevent="closeMenu?.()"
          class="ml-8 link__multi-user-access"
          :to="{ name: 'app.profile.multi-user-access' }"
        >
          <ui-icon size="14" :color="textColor" icon="fas fa-cog" />
        </router-link>
      </div>
    </div>
    <div class="list">
      <ui-checkbox
        v-for="workspace in allWorkspaces"
        :key="workspace.id"
        v-model="selectedWorkspaces"
        class="list-item"
        :color="$colors.gray40"
        :true-value="workspace.id"
      >
        <template #label>
          <div class="list-wrapper">
            <div class="list-item-text" :style="{ color: textColor }">
              {{ workspace.name }}
            </div>
            <div class="list-item-info" v-if="!store.user.isMember">
              <div class="list-item-info__inside">
                <template v-if="!store.user.isMember">
                  <template v-if="isMobile">
                    <ui-icon
                      size="10"
                      class="ml-8 mr-2"
                      :color="textColor"
                      icon="fas fa-user"
                    />
                    <div :style="{ color: textColor }">{{ workspace.membersCount }}</div>
                  </template>
                  <v-tooltip v-else location="bottom">
                    <template #activator="{ props: tooltipProps }">
                      <div class="list-item-info__item" v-bind="tooltipProps">
                        <ui-icon
                          size="10"
                          class="ml-8 mr-2"
                          :color="textColor"
                          icon="fas fa-user"
                        />
                        <div :style="{ color: textColor }">
                          {{ workspace.membersCount }}
                        </div>
                      </div>
                    </template>
                    <span> {{ t('Users amount') }} - {{ workspace.membersCount }} </span>
                  </v-tooltip>
                </template>
                <template v-if="isMobile">
                  <ui-icon
                    size="10"
                    class="ml-8 mr-2"
                    :color="textColor"
                    icon="fas fa-bullseye-arrow"
                  />
                  <div :style="{ color: textColor }">{{ workspace.itemsCount }}</div>
                </template>
                <v-tooltip v-else location="bottom">
                  <template #activator="{ props: tooltipProps }">
                    <div class="list-item-info__item" v-bind="tooltipProps">
                      <ui-icon
                        size="10"
                        class="ml-8 mr-2"
                        :color="textColor"
                        icon="fas fa-bullseye-arrow"
                      />
                      <div :style="{ color: textColor }">{{ workspace.itemsCount }}</div>
                    </div>
                  </template>
                  <span>
                    {{ t('Campaigns') }} - {{ workspace.itemsCountDetails.campaigns
                    }}<br />
                    {{ t('Offers') }} - {{ workspace.itemsCountDetails.offers }}<br />
                    {{ t('Landings') }} - {{ workspace.itemsCountDetails.landings }}<br />
                    {{ t('Flows') }} - {{ workspace.itemsCountDetails.flows }}<br />
                    {{ t('Traffic Sources') }} -
                    {{ workspace.itemsCountDetails.trafficSources }}<br />
                    {{ t('Affiliate Networks') }} -
                    {{ workspace.itemsCountDetails.affiliateNetworks }}
                  </span>
                </v-tooltip>
              </div>
            </div>
          </div>
        </template>
      </ui-checkbox>
    </div>

    <div style="display: flex" class="justify-end mt-4 mb-4 mr-8">
      <ui-button
        :disabled="selectedWorkspaces.length === 0"
        icon="fas fa-check"
        :text="t('Apply Changes')"
        button-style="outline-accent"
        @click="apply"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useColors, useI18n, useUrl } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import type { UserWorkspace } from '@/services/models';

const props = withDefaults(
  defineProps<{
    closeMenu?: () => void;
    isMobile?: boolean;
  }>(),
  {
    isMobile: false,
  },
);

const store = useStore();
const $colors = useColors();
const { t } = useI18n();
const { isDashboard } = useUrl();

const allWorkspaces = ref<UserWorkspace[]>([]);
const selectedWorkspaces = ref<string[]>([]);

const isSelectedAll = computed({
  get: () => {
    const workspaces = store.user.workspaces ?? [];
    return selectedWorkspaces.value.length === workspaces.length;
  },
  set: (value: boolean) => {
    const workspaces = store.user.workspaces ?? [];
    if (value) {
      selectedWorkspaces.value = workspaces.map(workspace => workspace.id);
    } else {
      selectedWorkspaces.value = [];
    }
  },
});

const textColor = computed(() => (props.isMobile ? $colors.gray20 : $colors.graphite));

const apply = () => {
  const workspaceIds = [...selectedWorkspaces.value];
  $emitter.emit('reset-pagination');
  store.mergeUser({ selectedWorkspaces: workspaceIds });
  props.closeMenu?.();
  if (isDashboard.value) {
    $emitter.emit('refresh-dashboard');
  }
};

watch(
  () => store.user,
  user => {
    if (!user) return;
    const { workspaces, selectedWorkspaces: selectedWorkspaceIds } = user;
    if (workspaces) {
      allWorkspaces.value = [...workspaces];
    }
    if (selectedWorkspaceIds) {
      selectedWorkspaces.value = [...selectedWorkspaceIds];
    }
  },
  { immediate: true, deep: true },
);
</script>

<style lang="scss" scoped>
:deep(.v-menu__activator) {
  height: 100% !important;
}

.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px 4px 8px;
  border-bottom: 1px solid var(--color-gray40);
}

:deep(.list-item) {
  white-space: nowrap;

  .v-input__control {
    width: 100%;
  }

  .v-label {
    justify-content: space-between;
    flex-grow: 1;
    width: 0;
  }
}

.list-wrapper {
  display: flex;
  flex: 1;
  justify-content: space-between;
  overflow: hidden;
}

.list {
  padding: 8px 0;
  max-height: 150px;
  overflow-y: auto;
}

.list-item-text {
  white-space: break-spaces;
}

.list-item {
  padding: 2px 13px 2px 8px;
  width: 100%;
  align-items: flex-start;

  :deep(.label-wrapper) {
    width: 100%;
    margin-top: -2px;
  }
}

.list-item-info {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  font-size: 10px;
  margin-top: 2px;
  color: var(--color-gray40);

  &__inside {
    display: flex;
    align-items: center;
  }

  &__item {
    display: flex;
    align-items: center;
  }
}

.link__multi-user-access {
  text-decoration: none;
}

.apply_changes {
  float: right;
  border: 1px solid var(--color-green) !important;
  font-size: 12px;
  margin: 12px 6px 12px 0;
}

.menu-body--mobile {
  background-color: var(--color-mobile-menu-bg);
  color: var(--color-gray20) !important;

  .list-item-text {
    :deep(.ui-label) {
      color: var(--color-gray20) !important;
    }
  }
}

.all-workspaces {
  width: 100px;
}
</style>
